
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  getPainelTextDefault,
  type PainelTextValue,
} from '@/lib/painel-texts';
import * as panelTextsService from '@/services/panel-texts.service';

type Overrides = Record<string, Partial<PainelTextValue>>;

type PainelTextsContextValue = {
  hydrated: boolean;
  getText: (key: string) => PainelTextValue;
  getOverride: (key: string) => Partial<PainelTextValue> | undefined;
  setText: (key: string, patch: Partial<PainelTextValue>) => void;
  resetText: (key: string) => void;
  isOverridden: (key: string) => boolean;
};

const PainelTextsContext = createContext<PainelTextsContextValue | null>(null);

export function PainelTextsProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverrides] = useState<Overrides>({});
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const entries = await panelTextsService.getPanelTexts();
        if (!cancelled && entries.length > 0) {
          const map: Overrides = {};
          for (const entry of entries) {
            map[entry.key] = { title: entry.title, description: entry.description };
          }
          setOverrides(map);
        }
      } catch {
        // defaults
      }
      if (!cancelled) setHydrated(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const getText = useCallback(
    (key: string): PainelTextValue => {
      const def = getPainelTextDefault(key);
      const ov = overrides[key];
      if (!ov) return def;
      return {
        title: ov.title != null && ov.title.trim() !== '' ? ov.title : def.title,
        description:
          ov.description != null && ov.description.trim() !== '' ? ov.description : def.description,
      };
    },
    [overrides]
  );

  const getOverride = useCallback((key: string) => overrides[key], [overrides]);

  const setText = useCallback((key: string, patch: Partial<PainelTextValue>) => {
    setOverrides((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }, []);

  const resetText = useCallback((key: string) => {
    setOverrides((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const isOverridden = useCallback((key: string) => key in overrides, [overrides]);

  const value = useMemo<PainelTextsContextValue>(
    () => ({ hydrated, getText, getOverride, setText, resetText, isOverridden }),
    [hydrated, getText, getOverride, setText, resetText, isOverridden]
  );

  return <PainelTextsContext.Provider value={value}>{children}</PainelTextsContext.Provider>;
}

/** Retorna {title, description} do painel — override do Super Admin ou o valor padrão. */
export function usePainelText(key: string): PainelTextValue {
  const ctx = useContext(PainelTextsContext);
  if (!ctx) return getPainelTextDefault(key);
  return ctx.getText(key);
}

/** API completa para o editor de textos (Super Admin). */
export function usePainelTextsAdmin(): PainelTextsContextValue {
  const ctx = useContext(PainelTextsContext);
  if (!ctx) {
    throw new Error('usePainelTextsAdmin must be used within PainelTextsProvider');
  }
  return ctx;
}
